/**
 * @param {string} type - The type of the response to be returned. Default is 'json'.
 * @returns {Object} - The configuration object for the fetch request.
 */
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/**
 * Fetch HTML from a URL
 * @param {string} url - The URL to fetch HTML from
 * @returns {Promise<Document>} - The HTML document
 */
async function fetchHTML(url) {
  const response = await fetch(url);
  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

function getElementDimensions(element = null) {
  if (!element) return;

  const elementDom = document.querySelector(element);
  const rect = elementDom.getBoundingClientRect();
  return rect;
}

/**
 * Get all focusable elements inside a container
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getFocusableElements(container) {
  if (!container?.querySelectorAll) return [];

  try {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    ).filter((element) => {
      try {
        for (let current = element; current; current = current.parentElement) {
          const style = getComputedStyle(current);
          if (style.display === 'none' || style.visibility === 'hidden') return false;
        }
        return true;
      } catch {
        return false;
      }
    });
  } catch {
    return [];
  }
}

/**
 * Get the index of a node in its parent
 * @param {HTMLElement} node
 * @returns {number}
 */
function getNodeIndex(node) {
  return Array.from(node.parentNode.children).indexOf(node);
}

/**
 * Detects a click outside of a given element
 * @param {HTMLElement} element - The element to detect a click outside of
 * @param {Function} callback - The callback to execute when a click is detected outside of the element
 * @returns {Function} - A function to remove the event listener
 * @example
 * this.drawer = this.querySelector('drawer');
 * onClickOutside(this.drawer, () => this.close());
 */
function onClickOutside(element, callback) {
  const handleClick = (event) => {
    if (element && !element.contains(event.target)) callback();
  };

  document.addEventListener('mousedown', handleClick);

  return () => {
    document.removeEventListener('mousedown', handleClick);
  };
}

export { fetchConfig, fetchHTML, getElementDimensions, getFocusableElements, getNodeIndex, onClickOutside };
